import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getLandingPage, getLandingPageComponents, apiClient } from '../services/api';
import LPComponentRenderer, { BrandTheme } from '../components/LandingPage/LPComponentRenderer';

/**
 * Build a brand-aware preview of a landing page from its component tree.
 *
 * Why this exists: GET /api/landing-pages/{id}/preview returns a stale
 * `lp.html_content` snapshot in the legacy single-template format. The "real"
 * preview the LP module shows is rendered client-side from `lp.components` +
 * brand theme via <LPComponentRenderer>, captured to HTML, then served back
 * via /api/landing-pages/preview-temp/{uid}. This hook encapsulates that
 * pipeline so any caller (campaign LP card, future surfaces) can reuse it
 * without copy-pasting ~80 lines of capture/wrap/POST logic.
 *
 * Usage:
 *   const { open, close, previewUrl, isLoading, HiddenCapture } = useLandingPagePreview();
 *   ...
 *   <button onClick={() => open(landingPageId)}>Preview</button>
 *   {previewUrl && <iframe src={previewUrl} />}
 *   <HiddenCapture />     // mount once anywhere — required for capture to work
 */
export function useLandingPagePreview() {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [captureData, setCaptureData] = useState<{ components: any[]; title: string; brandTheme?: BrandTheme } | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);

  const open = useCallback(async (landingPageId: string) => {
    setIsLoading(true);
    try {
      const [components, fullPage] = await Promise.all([
        getLandingPageComponents(landingPageId),
        getLandingPage(landingPageId),
      ]);
      if (!components.length) {
        alert('No preview available yet — open this page in the editor first.');
        return;
      }
      const tc = (fullPage as any).theme_config || {};
      let brandTheme: BrandTheme = {
        primaryColor: tc.brand_primary_color || tc.primary_color || '#2177FF',
        secondaryColor: tc.brand_secondary_color || '#1EACA6',
        fontFamily: tc.brand_primary_font || 'inherit',
        logoUrl: tc.logo_url || '',
        companyName: tc.company_name || '',
      };
      const pid = fullPage.project_id;
      if (pid && pid !== 'unassigned') {
        try {
          let bg: any = null;
          try {
            const r = await apiClient.get(`/brand-guides/project/${pid}/default`);
            bg = r.data;
          } catch {
            const r2 = await apiClient.get(`/brand-guides/project/${pid}`);
            if (r2.data?.length > 0) bg = r2.data[0];
          }
          if (bg) {
            brandTheme = {
              primaryColor: bg.primary_color || brandTheme.primaryColor,
              secondaryColor: bg.secondary_color || brandTheme.secondaryColor,
              accentColor: bg.accent_color || undefined,
              fontFamily: bg.primary_font || bg.secondary_font || brandTheme.fontFamily,
              logoUrl: bg.logo_url || brandTheme.logoUrl,
              companyName: bg.brand_name || brandTheme.companyName,
            };
          }
        } catch { /* keep defaults */ }
      }
      setCaptureData({ components, title: fullPage.title, brandTheme });
    } catch {
      alert('Failed to load preview.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const close = useCallback(() => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewTitle('');
  }, [previewUrl]);

  useEffect(() => {
    if (!captureData || !captureRef.current) return;
    const timer = setTimeout(async () => {
      const div = captureRef.current;
      if (!div) return;
      const origin = window.location.origin;
      let inner = div.innerHTML;
      inner = inner.replace(
        /<iframe[^>]+src=["'][^"']*\/quiz-embed\/([^?'"]+)\?([^"']*)["'][^>]*><\/iframe>/g,
        (_match, quizId, queryString) => {
          const qp = new URLSearchParams(queryString.replace(/&amp;/g, '&'));
          const pc = qp.get('primary_color') || '#2177FF';
          const sc = qp.get('secondary_color') || '';
          const ff = qp.get('font_family') || '';
          return `<aime-quiz quiz-id="${quizId}" primary-color="${pc}"${sc ? ` secondary-color="${sc}"` : ''}${ff ? ` font-family="${ff}"` : ''} style="display:block;max-width:680px;margin:0 auto"></aime-quiz>`;
        }
      );
      const btnHoverCss = `html{scroll-behavior:smooth}.lp-btn{transition:filter .18s ease,transform .18s ease;cursor:pointer}.lp-btn:hover{filter:brightness(.88);transform:translateY(-1px) scale(1.02)}.lp-btn:active{filter:brightness(.78);transform:translateY(0) scale(.99)}.lp-btn-ghost{transition:background .18s ease,transform .18s ease;cursor:pointer}.lp-btn-ghost:hover{background:rgba(255,255,255,.28)!important;transform:translateY(-1px)}.lp-btn-ghost:active{transform:translateY(0)}`;
      const font = captureData.brandTheme?.fontFamily;
      const systemFonts = ['inherit', 'sans-serif', 'serif', 'monospace', '-apple-system'];
      const isCustomFont = font && !systemFonts.some(sf => font.toLowerCase().includes(sf));
      const fontLink = isCustomFont
        ? `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(font!)}:wght@400;500;600;700;800&display=swap">`
        : '';
      const fontFamily = font && font !== 'inherit' ? `'${font}', ` : '';
      const fullHtml = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${captureData.title}</title>${fontLink}<link rel="stylesheet" href="${origin}/widgets/style.css"><style>body{margin:0;font-family:${fontFamily}-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}${btnHoverCss}</style></head><body>${inner}<script type="module" src="${origin}/widgets/aime-widgets.js"></script></body></html>`;
      try {
        const res = await fetch('/api/landing-pages/preview-temp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: fullHtml }),
        });
        const { uid } = await res.json();
        setPreviewTitle(captureData.title);
        setPreviewUrl(`/api/landing-pages/preview-temp/${uid}`);
      } catch {
        const blob = new Blob([fullHtml], { type: 'text/html' });
        setPreviewTitle(captureData.title);
        setPreviewUrl(URL.createObjectURL(blob));
      }
      setCaptureData(null);
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureData]);

  // Off-screen capture mount point — caller renders this once anywhere in
  // their tree; the hook owns its content.
  const HiddenCapture: React.FC = () => {
    if (!captureData) return null;
    return (
      <div
        ref={captureRef}
        style={{ position: 'fixed', left: '-9999px', top: 0, width: '1200px', visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}
      >
        {captureData.components.map((comp: any) => (
          <LPComponentRenderer key={comp.id} component={comp} isSelected={false} brandTheme={captureData.brandTheme} />
        ))}
      </div>
    );
  };

  return { open, close, previewUrl, previewTitle, isLoading, HiddenCapture };
}
